/**
 * Workspace API component DTOs, mirrored 1:1 from WorkspaceAPI/src/Adapter/API/Scenes/{ComponentsInput,FilterModel,
 * VisualizationModel,ColoringModel,SizeModel,RangeSetModel}.cs. Lite's store holds these shapes directly so that
 * "Open in Nanome" serialises state without a mapper. Rules (verified against the server converters):
 *  - enums are strings (case-insensitive on input, numbers rejected)
 *  - colours are 'RRGGBB' with no '#'
 *  - `size` and `coloring` are mandatory on every visualization (the server dereferences them)
 *  - serial sets are { ranges: [[a, b], ...], sets: [n, ...] }
 *  - a filter list is a pipeline: each filter narrows; Union/Intersect/Except run containedFilters from all atoms
 */

export type HexString = string; // 'RRGGBB'

export interface RangeSet { ranges: [number, number][]; sets: number[] }

export type FilterKind =
  | 'EntrySerial' | 'Substructure' | 'ConnectedResidue' | 'ModelIndex' | 'EnumeratedAtoms' | 'ResidueNames' | 'Distance'
  | 'Subsurface' | 'CurrentModel' | 'InterEntryDistance' | 'Union' | 'Intersect' | 'ChainNames' | 'ResidueSerials'
  | 'Except' | 'AtomNames' | 'AltLocs' | 'Residues' | 'ModelSerials';

/** ESubstructureFilterType is a [Flags] enum; the wire form is the C# ToString, e.g. 'Protein' or 'DNA, RNA, PNA' or 'Nucleic'. */
export type SubstructureFilterType = 'Protein' | 'DNA' | 'RNA' | 'PNA' | 'Ligand' | 'Saccharide' | 'Ion' | 'Solvent' | 'Nucleic' | 'Polymer' | (string & {});

export interface ResidueRef { residueSerial: number; insertionCode?: number }

export interface FilterModel {
  kind: FilterKind;
  entrySerial?: number;
  substructureType?: SubstructureFilterType;
  serialRangeSet?: RangeSet;
  onlyWhenSceneModelMatches?: boolean;
  names?: string[];
  containedFilters?: FilterModel[];
  distance?: number;
  wholeResidue?: boolean;
  ignoreSelf?: boolean;
  residues?: ResidueRef[];
  occupancySelection?: 'Highest' | 'Lowest' | 'Custom';
}

export type SizeOption = 'NoSize' | 'Uniform' | 'Physical' | 'Disorder';
export interface SizeModel { sizeOption: SizeOption; scale: number }

export type ColoringKind = 'ElementType' | 'Property' | 'ResidueType' | 'SecondaryStructure' | 'Uniform' | 'Enumerated' | 'HydrogenBonding' | 'Electrostatic';
export type PropertySource = 'AtomName' | 'AtomSerial' | 'BFactor' | 'Occupancy' | 'ChainName' | 'ChainInstance' | 'ModelInstance';
export type PaletteType = 'Default' | 'Interpolated';

export interface ColorRangeSet { color: HexString; serials: RangeSet }

export interface ColoringModel {
  kind: ColoringKind;
  carbonsOnly?: boolean;
  colors?: HexString[];
  colorRanges?: ColorRangeSet[];
  propertySource?: PropertySource;
  paletteType?: PaletteType;
  uniformColor?: HexString;
  rampMin?: number;
  rampMax?: number;
}

export type VisualizationKind = 'Atomistic' | 'MolecularSurface' | 'Ribbon' | 'ResidueLabel' | 'Interaction';
export type AtomRenderMode = 'All' | 'NonBondedOnly' | 'None';
export type HydrogenRenderMode = 'All' | 'PolarOnly' | 'None';
export type RibbonStyle = 'Cartoon' | 'Coil';
export type InteractionType = 'HydrogenBonds' | 'HydrogenBondsPolymerPolymer' | 'PiStacking' | 'Clash' | 'Halogen' | 'PiCation' | 'Hydrophobic' | 'SaltBridges' | 'MetalCoordination' | 'Other' | 'HalogenPi';

export interface VisualizationModel {
  kind: VisualizationKind;
  visible: boolean;
  size: SizeModel;
  coloring: ColoringModel;
  renderAtom?: AtomRenderMode;
  renderBond?: boolean;
  bondScale?: number;
  renderHydrogen?: HydrogenRenderMode;
  gapScale?: number;
  interactionType?: InteractionType;
  opacity?: number;
  subsurface?: boolean;
  wireframe?: boolean;
  ribbonStyle?: RibbonStyle;
  ligandToPocketOnly?: boolean;
}

/** Body of POST /workspaces/{id}/scenes/{s}/components/add (one per call). No `hidden` field: use toggle-hidden. */
export interface ComponentInput { name: string; filters: FilterModel[]; visualizations: VisualizationModel[] }

/** Server defaults when a field is omitted (ApiController VisualizationConverter). */
export const VISUALIZATION_DEFAULTS = {
  renderAtom: 'All', renderBond: false, bondScale: 0, renderHydrogen: 'All', opacity: 1, subsurface: false, wireframe: false, ribbonStyle: 'Cartoon', interactionType: 'HydrogenBonds',
} as const satisfies Partial<VisualizationModel>;

export const NO_SIZE: SizeModel = { sizeOption: 'NoSize', scale: 0 };
